import { getFieldState } from '@/shared/lib/forms';
import { Button } from '@/shared/ui/shadcn/button';
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from '@/shared/ui/shadcn/field';

import type { RxFormApi } from '../../model/types';
import { usePharmacySelection } from '../../model/usePharmacySelection';
import { PharmacySearchDialog } from './PharmacySearchDialog';

interface Props {
  form: RxFormApi;
  userId: string;
}

export function PharmacySelector({ form, userId }: Props) {
  const {
    addError,
    addFromSearchResult,
    addPatientPharmacyMutation,
    closeSearch,
    isSearchOpen,
    openSearch,
    patientPharmaciesQuery,
    pendingAddId,
    pendingSetPrimaryId,
    pharmacySearchQuery,
    savedPharmacies,
    savedPharmaciesById,
    searchFields,
    searchResults,
    setSearchFields,
    setAsPrimary,
    setPrimaryError,
  } = usePharmacySelection(userId, form);
  const resetSearchFields = () => {
    setSearchFields({
      name: '',
      city: '',
      state: '',
      zip: '',
      specialty: [],
    });
  };

  return (
    <FieldGroup>
      <form.Field
        name="selectedPharmacy"
        children={(field) => {
          const { isInvalid, errors } = getFieldState(field.state.meta);
          const selectedPharmacy = field.state.value;
          const selectedPharmacyId = selectedPharmacy?.PharmacyId;
          const selectedPharmacyLocation = selectedPharmacy
            ? [selectedPharmacy.City, selectedPharmacy.State, selectedPharmacy.ZipCode]
                .filter(
                  (value): value is string => typeof value === 'string' && value.trim().length > 0
                )
                .join(', ')
            : '';
          const selectedPharmacyAddress = selectedPharmacy
            ? [selectedPharmacy.Address1, selectedPharmacy.Address2, selectedPharmacyLocation]
                .filter(
                  (value): value is string => typeof value === 'string' && value.trim().length > 0
                )
                .join(', ')
            : '';

          // Check if selected pharmacy is in the patient's saved list (for "Set as primary" feature)
          const savedMatch =
            selectedPharmacyId != null
              ? savedPharmaciesById.get(String(selectedPharmacyId))
              : undefined;
          const isSelectedInSavedList = savedMatch != null;

          // Show "Set as primary" only for saved pharmacies that aren't already primary
          const canSetAsPrimary =
            isSelectedInSavedList &&
            !savedMatch.isPrimary &&
            !patientPharmaciesQuery.isPending &&
            !patientPharmaciesQuery.isError;

          return (
            <Field orientation="vertical" data-invalid={isInvalid}>
              <FieldContent>
                <FieldLabel htmlFor={field.name}>Pharmacy</FieldLabel>
                <FieldDescription>
                  {patientPharmaciesQuery.isPending
                    ? 'Loading saved pharmacies…'
                    : patientPharmaciesQuery.isError
                      ? 'Could not load saved pharmacies.'
                      : savedPharmacies.length === 0
                        ? 'No saved pharmacies found.'
                        : ''}
                </FieldDescription>
                {isInvalid ? <FieldError errors={errors} /> : null}
              </FieldContent>
              <div className="flex w-full flex-col gap-2 @md/field-group:w-auto">
                <PharmacySearchDialog
                  open={isSearchOpen}
                  onOpenChange={(open) => {
                    if (open) {
                      openSearch();
                      return;
                    }
                    closeSearch();
                  }}
                  searchFields={searchFields}
                  onSearchFieldsChange={setSearchFields}
                  onCloseResetSearchText={resetSearchFields}
                  isSearchPending={pharmacySearchQuery.isPending}
                  isSearchError={pharmacySearchQuery.isError}
                  onRetrySearch={() => void pharmacySearchQuery.refetch()}
                  savedPharmacies={savedPharmacies}
                  selectedSavedPharmacyId={savedMatch?.pharmacyId ?? null}
                  onSelectSavedPharmacy={(pharmacyId) => {
                    const match = savedPharmaciesById.get(String(pharmacyId));
                    if (!match) {
                      return;
                    }
                    field.handleChange(match.rxPharmacy);
                    closeSearch();
                  }}
                  searchResults={searchResults}
                  addError={addError}
                  isAddPending={addPatientPharmacyMutation.isPending}
                  pendingAddId={pendingAddId}
                  onAddPharmacy={(pharmacy) => void addFromSearchResult(pharmacy)}
                  triggerLabel={
                    selectedPharmacy
                      ? `${selectedPharmacy.StoreName ?? `Pharmacy ${selectedPharmacyId}`} — ${selectedPharmacyAddress || 'No address'}`
                      : 'Search pharmacies'
                  }
                  triggerDisabled={addPatientPharmacyMutation.isPending}
                />

                {canSetAsPrimary && savedMatch ? (
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    disabled={
                      pendingSetPrimaryId === savedMatch.pharmacyId ||
                      addPatientPharmacyMutation.isPending
                    }
                    onClick={() => void setAsPrimary(savedMatch.pharmacyId)}
                  >
                    {pendingSetPrimaryId === savedMatch.pharmacyId
                      ? 'Setting…'
                      : 'Set as primary pharmacy'}
                  </Button>
                ) : null}

                {setPrimaryError ? (
                  <div className="text-sm text-destructive">{setPrimaryError}</div>
                ) : null}

                {patientPharmaciesQuery.isError ? (
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => void patientPharmaciesQuery.refetch()}
                  >
                    Retry
                  </Button>
                ) : null}
              </div>
            </Field>
          );
        }}
      />
    </FieldGroup>
  );
}
